import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, KeyRound, X } from 'lucide-react';
import type { OltCredentialCheckResult, SmartoltMigrationItem } from '@simpleacs/shared';
import { api } from '../../lib/api';
import {
  blankCredentialDraft,
  credentialBody,
  credentialDraftPartial,
  credentialDraftReady,
  type OltCredentialDraft,
} from '../../lib/smartolt-credentials';
import { Button, ErrorNotice, Input, Pill } from '../../components/ui';

/**
 * The OLTs a migration brings over that this platform could not read without
 * a login — SmartOLT's API never returns one. Typed here, checked here if the
 * operator wants (a login closed straight away and an SNMP read, nothing
 * stored), and sent with Apply: stored encrypted, never over a login already
 * held, then a first sweep that only reads.
 */
export function OltCredentialsPanel({
  items,
  drafts,
  onChange,
}: {
  items: SmartoltMigrationItem[];
  drafts: Record<string, OltCredentialDraft>;
  onChange: (key: string, draft: OltCredentialDraft) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section className="space-y-3 rounded-lg border border-line p-4">
      <header className="flex items-center gap-2">
        <KeyRound className="size-4 text-muted" />
        <h3 className="text-sm font-semibold text-ink">Logins for the OLTs</h3>
      </header>
      <p className="text-xs text-muted">
        SmartOLT’s API never returns an OLT’s login or SNMP communities, and without them SimpleACS
        cannot read the chassis — no ONU states, optics or VLANs of its own. Give the ones SmartOLT
        uses: they are stored encrypted with the OLT and each OLT is swept once, reading only. An OLT
        left blank is brought over without, and takes them from Edit later.
      </p>
      <div className="space-y-3">
        {items.map((item) => (
          <OltCredentialRow
            key={item.key}
            item={item}
            draft={drafts[item.key] ?? blankCredentialDraft(item)}
            onChange={(draft) => onChange(item.key, draft)}
          />
        ))}
      </div>
    </section>
  );
}

function Result({ label, check }: { label: string; check: { ok: boolean; detail: string } | null }) {
  if (!check) return null;
  return (
    <span className={`flex items-start gap-1.5 text-xs ${check.ok ? 'text-success' : 'text-danger'}`}>
      {check.ok ? <Check className="mt-0.5 size-3.5 shrink-0" /> : <X className="mt-0.5 size-3.5 shrink-0" />}
      <span>
        <span className="font-medium">{label}:</span> {check.detail}
      </span>
    </span>
  );
}

function OltCredentialRow({
  item,
  draft,
  onChange,
}: {
  item: SmartoltMigrationItem;
  draft: OltCredentialDraft;
  onChange: (draft: OltCredentialDraft) => void;
}) {
  const [result, setResult] = useState<OltCredentialCheckResult | null>(null);
  const set = (patch: Partial<OltCredentialDraft>) => {
    setResult(null);
    onChange({ ...draft, ...patch });
  };
  const check = useMutation({
    mutationFn: () =>
      api.post<OltCredentialCheckResult>('/olts/credential-check', {
        ...credentialBody(draft, item),
        vendor: item.olt!.vendor,
        mgmtIp: item.olt!.mgmtIp,
      }),
    onSuccess: setResult,
  });

  return (
    <div className="space-y-2 rounded-lg border border-line bg-surface-2 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
        <span className="font-medium">{item.label}</span>
        {item.olt && (
          <span className="font-mono text-xs text-muted">
            {item.olt.vendor === 'huawei' ? 'Huawei' : 'ZTE'} · {item.olt.mgmtIp}
          </span>
        )}
        {credentialDraftReady(draft) ? (
          <Pill tone="success">will be stored</Pill>
        ) : credentialDraftPartial(draft) ? (
          <Pill tone="warn">a login and a read community are needed</Pill>
        ) : (
          <Pill tone="neutral">left blank</Pill>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[7rem_5rem_1fr_1fr_1fr_1fr_auto]">
        <select
          className="input"
          value={draft.cliTransport}
          onChange={(e) => {
            const transport = e.target.value as OltCredentialDraft['cliTransport'];
            set({ cliTransport: transport, cliPort: transport === 'ssh' ? '22' : String(item.olt?.cliPort ?? 23) });
          }}
          aria-label="CLI transport"
        >
          <option value="telnet">Telnet</option>
          <option value="ssh">SSH</option>
        </select>
        <Input value={draft.cliPort} onChange={(e) => set({ cliPort: e.target.value })} aria-label="CLI port" placeholder="23" />
        <Input
          value={draft.cliUsername}
          onChange={(e) => set({ cliUsername: e.target.value })}
          placeholder="Username"
          autoComplete="off"
          aria-label="CLI username"
        />
        <Input
          type="password"
          value={draft.cliPassword}
          onChange={(e) => set({ cliPassword: e.target.value })}
          placeholder="Password"
          autoComplete="new-password"
          aria-label="CLI password"
        />
        <Input
          value={draft.snmpCommunity}
          onChange={(e) => set({ snmpCommunity: e.target.value })}
          placeholder="Read community"
          autoComplete="off"
          aria-label="SNMP read community"
          className="font-mono"
        />
        <Input
          value={draft.snmpCommunityRw}
          onChange={(e) => set({ snmpCommunityRw: e.target.value })}
          placeholder="Write community (optional)"
          autoComplete="off"
          aria-label="SNMP write community"
          className="font-mono"
        />
        <Button
          size="sm"
          disabled={!credentialDraftReady(draft) || !item.olt}
          loading={check.isPending}
          onClick={() => check.mutate()}
        >
          Check
        </Button>
      </div>
      {result && (
        <div className="grid gap-1 sm:grid-cols-3">
          <Result label="Login" check={result.cli} />
          <Result label="Read community" check={result.snmpRead} />
          <Result label="Write community" check={result.snmpWrite} />
        </div>
      )}
      {check.error ? <ErrorNotice error={check.error} /> : null}
    </div>
  );
}
